import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSCGRule, getSCGPdfUrl } from '../services/api';
import { Link } from 'react-router-dom';
import { buildPdfSandboxUrlFromParts } from '../utils/pdfSandboxUrl';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  DocumentPlusIcon,
  DocumentMagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const RejectedRulesReview = ({ rejectedRules, onClose, sourceFile }) => {
  const [rules, setRules] = useState(rejectedRules || []);
  const [expandedRules, setExpandedRules] = useState(new Set());
  const [editingRule, setEditingRule] = useState(null);
  const [approvedCount, setApprovedCount] = useState(0);
  const [dismissedCount, setDismissedCount] = useState(0);

  const queryClient = useQueryClient();

  // Get PDF viewer link for a rule
  const getPdfViewerLink = (rule) => {
    // Get PDF URL - MUST use API endpoint, not file path
    // Priority: 1) citation_display.pdf_url (if it's an API URL), 2) API endpoint with file_hash
    let pdfUrl = null;

    // Check if citation_display has a proper API URL
    if (rule.citation_display?.pdf_url && rule.citation_display.pdf_url.startsWith('/api/')) {
      pdfUrl = rule.citation_display.pdf_url;
    }
    // Otherwise use file_hash to construct the environment-aware API URL
    else if (rule.source_document?.file_hash) {
      pdfUrl = getSCGPdfUrl(rule.source_document.file_hash);
    }
    // Fallback: if stored_filename exists, extract hash from it
    else if (rule.source_document?.stored_filename) {
      const hash = rule.source_document.stored_filename.replace('.pdf', '');
      pdfUrl = getSCGPdfUrl(hash);
    }

    if (!pdfUrl) return null;

    const page = rule.location_metadata?.page_number || 1;

    // Rejected rules aren't persisted yet, so there's no ruleId to pass —
    // the sandbox viewer only uses ruleId for the context strip + a
    // cosmetic filename fetch, it isn't required to render the PDF.
    return buildPdfSandboxUrlFromParts({
      pdfUrl,
      page,
      title: rule.item || rule.enhanced_description?.substring(0, 50) || 'Flagged Rule',
      q: rule.item || rule.enhanced_description,
      level: rule.classification_level,
    });
  };

  // Get page number from rule
  const getPageNumber = (rule) => {
    return rule.location_metadata?.page_number || null;
  };

  const createMutation = useMutation({
    mutationFn: createSCGRule,
    onSuccess: (data, variables) => {
      // Remove the approved rule from the list
      setRules(prev => prev.filter(r => r !== variables._originalRule));
      setApprovedCount(prev => prev + 1);
      queryClient.invalidateQueries(['scg-rules-search']);
      queryClient.invalidateQueries(['scg-sources']);
    },
    onError: (error) => {
      console.error('Failed to create rule:', error);
    }
  });

  const toggleExpand = (index) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRules(newExpanded);
  };

  const handleApprove = (rule) => {
    // Clean up internal fields before sending to API
    const ruleData = { ...rule };
    delete ruleData._rejection_reason;
    delete ruleData._needs_review;
    delete ruleData._originalRule;

    // Add source file reference
    ruleData.source = sourceFile || ruleData.source || 'manual_review';

    // Ensure description is set
    if (!ruleData.description && ruleData.enhanced_description) {
      ruleData.description = ruleData.enhanced_description;
    }
    if (!ruleData.description && ruleData.item) {
      ruleData.description = ruleData.item;
    }

    createMutation.mutate({ ...ruleData, _originalRule: rule });
  };

  const handleDismiss = (rule) => {
    setRules(prev => prev.filter(r => r !== rule));
    setDismissedCount(prev => prev + 1);
  };

  const handleEdit = (rule, index) => {
    setEditingRule({ rule: { ...rule }, index });
  };

  const handleSaveEdit = () => {
    if (editingRule) {
      const newRules = [...rules];
      newRules[editingRule.index] = editingRule.rule;
      setRules(newRules);
      setEditingRule(null);
    }
  };

  const handleEditChange = (field, value) => {
    setEditingRule(prev => ({
      ...prev,
      rule: { ...prev.rule, [field]: value }
    }));
  };

  const handleApproveAll = () => {
    rules.forEach(rule => handleApprove(rule));
  };

  const handleDismissAll = () => {
    setDismissedCount(prev => prev + rules.length);
    setRules([]);
  };

  if (rules.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircleIcon className="w-6 h-6 text-green-500 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-green-900">Review Complete</h3>
              <p className="text-sm text-green-700">
                {approvedCount} rule(s) approved, {dismissedCount} rule(s) dismissed
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-green-600 hover:text-green-800"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-yellow-900">
              {rules.length} Rule(s) Need Review
            </h3>
            <p className="text-sm text-yellow-700">
              These rules were flagged during extraction. Review and approve or dismiss them.
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleApproveAll}
            disabled={createMutation.isPending}
            className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            Approve All
          </button>
          <button
            onClick={handleDismissAll}
            className="flex items-center px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            <XMarkIcon className="w-4 h-4 mr-1" />
            Dismiss All
          </button>
          <button
            onClick={onClose}
            className="text-yellow-600 hover:text-yellow-800 p-1"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Progress */}
      {(approvedCount > 0 || dismissedCount > 0) && (
        <div className="mb-4 text-sm text-yellow-700">
          Progress: {approvedCount} approved, {dismissedCount} dismissed, {rules.length} remaining
        </div>
      )}

      {/* Rules List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {rules.map((rule, index) => {
          const isExpanded = expandedRules.has(index);
          const isEditing = editingRule?.index === index;

          return (
            <div
              key={index}
              className="bg-white border border-yellow-200 rounded-lg p-4"
            >
              {/* Rule Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      rule.classification_level === 'TOP SECRET' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                      rule.classification_level === 'SECRET' ? 'bg-red-100 text-red-800 border border-red-300' :
                      rule.classification_level === 'CONFIDENTIAL' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                      rule.classification_level === 'UNCLASSIFIED' ? 'bg-green-100 text-green-800 border border-green-300' :
                      'bg-gray-100 text-gray-800 border border-gray-300'
                    }`}>
                      {rule.classification_level || 'UNKNOWN'}
                    </span>
                    <button
                      onClick={() => toggleExpand(index)}
                      className="text-xs text-yellow-600 hover:text-yellow-800 flex items-center"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUpIcon className="w-3 h-3 mr-1" />
                          Less
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="w-3 h-3 mr-1" />
                          More
                        </>
                      )}
                    </button>
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editingRule.rule.item || ''}
                        onChange={(e) => handleEditChange('item', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        placeholder="Item/Subject"
                      />
                      <textarea
                        value={editingRule.rule.enhanced_description || editingRule.rule.description || ''}
                        onChange={(e) => handleEditChange('enhanced_description', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        rows={2}
                        placeholder="Description"
                      />
                      <select
                        value={editingRule.rule.classification_level || ''}
                        onChange={(e) => handleEditChange('classification_level', e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded"
                      >
                        <option value="">Select Level</option>
                        <option value="UNCLASSIFIED">UNCLASSIFIED</option>
                        <option value="CONFIDENTIAL">CONFIDENTIAL</option>
                        <option value="SECRET">SECRET</option>
                        <option value="TOP SECRET">TOP SECRET</option>
                      </select>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveEdit}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingRule(null)}
                          className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {rule.item || 'No item specified'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {rule.enhanced_description || rule.description || 'No description'}
                      </p>
                    </>
                  )}

                  {/* Rejection Reason and Source Link */}
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      <span className="font-medium">Rejection reason:</span>
                      <span className="ml-1">{rule._rejection_reason || 'Unknown'}</span>
                    </div>

                    {/* View Source Link */}
                    {getPdfViewerLink(rule) && (
                      <Link
                        to={getPdfViewerLink(rule)}
                        target="_blank"
                        className="flex items-center text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100"
                      >
                        <DocumentMagnifyingGlassIcon className="w-3 h-3 mr-1" />
                        View Source
                        {getPageNumber(rule) && (
                          <span className="ml-1 text-blue-500">(p. {getPageNumber(rule)})</span>
                        )}
                      </Link>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-1 ml-4">
                  <button
                    onClick={() => handleEdit(rule, index)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    title="Edit"
                    disabled={isEditing}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleApprove(rule)}
                    disabled={createMutation.isPending}
                    className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                    title="Approve and Index"
                  >
                    {createMutation.isPending ? (
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    ) : (
                      <DocumentPlusIcon className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDismiss(rule)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    title="Dismiss"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && !isEditing && (
                <div className="mt-3 pt-3 border-t border-yellow-100 grid grid-cols-2 gap-2 text-xs">
                  {rule.derivative_citation && (
                    <div>
                      <span className="font-medium text-gray-700">Citation:</span>
                      <span className="ml-1 text-gray-600">{rule.derivative_citation}</span>
                    </div>
                  )}
                  {rule.reason && (
                    <div>
                      <span className="font-medium text-gray-700">Reason:</span>
                      <span className="ml-1 text-gray-600">{rule.reason}</span>
                    </div>
                  )}
                  {rule.declassify_on && (
                    <div>
                      <span className="font-medium text-gray-700">Declassify:</span>
                      <span className="ml-1 text-gray-600">{rule.declassify_on}</span>
                    </div>
                  )}
                  {rule.context_section && (
                    <div>
                      <span className="font-medium text-gray-700">Section:</span>
                      <span className="ml-1 text-gray-600">{rule.context_section}</span>
                    </div>
                  )}
                  {rule.context_agency && (
                    <div>
                      <span className="font-medium text-gray-700">Agency:</span>
                      <span className="ml-1 text-gray-600">{rule.context_agency}</span>
                    </div>
                  )}
                  {rule.context_domain && (
                    <div>
                      <span className="font-medium text-gray-700">Domain:</span>
                      <span className="ml-1 text-gray-600">{rule.context_domain}</span>
                    </div>
                  )}
                  {rule.keywords && rule.keywords.length > 0 && (
                    <div className="col-span-2">
                      <span className="font-medium text-gray-700">Keywords:</span>
                      <span className="ml-1 text-gray-600">{rule.keywords.join(', ')}</span>
                    </div>
                  )}
                  {rule.remarks && (
                    <div className="col-span-2">
                      <span className="font-medium text-gray-700">Remarks:</span>
                      <span className="ml-1 text-gray-600">{rule.remarks}</span>
                    </div>
                  )}
                  {/* Source Location */}
                  {rule.location_metadata && (
                    <div className="col-span-2 mt-2 pt-2 border-t border-yellow-100">
                      <span className="font-medium text-gray-700">Source Location:</span>
                      <div className="mt-1 text-gray-600 bg-gray-50 p-2 rounded">
                        {rule.location_metadata.page_number && (
                          <span className="mr-3">Page {rule.location_metadata.page_number}</span>
                        )}
                        {rule.location_metadata.extraction_method && (
                          <span className="text-gray-500">({rule.location_metadata.extraction_method})</span>
                        )}
                        {rule.source_document?.filename && (
                          <div className="mt-1 text-xs text-gray-500">
                            From: {rule.source_document.filename}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RejectedRulesReview;
